import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbIconConfig,
  NbInputModule,
  NbOptionModule,
  NbPopoverModule,
  NbSelectModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as R from 'ramda';
import { combineLatest, filter, first, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { catchError, defaultIfEmpty, map, switchMap, take, tap, timeout } from 'rxjs/operators';
import { APIService, KPI } from '../../../@core/api';
import { GROUP_PERMISSION_ACCESS_AUDIT } from '../../../@core/api/models';
import {
  AssetsChangesService,
  AssetsService,
  ConfigService,
  DocumentsService,
  DocumentTypesService,
  InvestmentsService,
  MaintenanceService,
  SaveToastService,
  ScopeService,
  SettingsService,
  StateLevelsService,
  UsersService,
} from '../../../@core/utils';
import { getColorHex } from '../../../@core/utils/color.utils';
import { CustomToggleComponent } from '../custom-toggle/custom-toggle.component';
import { ImagePreviewDialogComponent } from '../image-preview-dialog/image-preview-dialog.component';
import { InvestmentListDrawerComponent } from '../investment-list-drawer/investment-list-drawer.component';
import { InvestmentFormComponent } from '../side-forms/investment-form/investment-form.component';
import { ComplianceTabComponent } from './compliance-tab/compliance-tab.component';
import { DescriptifTabComponent } from './descriptif-tab/descriptif-tab.component';
import { DocumentsTabComponent } from './documents-tab/documents-tab.component';
import { MaintenanceTabComponent } from './maintenance-tab/maintenance-tab.component';
import { SummaryTabComponent } from './summary-tab/summary-tab.component';
// import { SheetDialogComponent } from "../sheet-dialog/sheet-dialog.component";
import { BasicDialogComponent } from '../basic-dialog/basic-dialog.component';
import { SideFormDialogComponent } from '../side-forms/side-form-dialog/side-form-dialog.component';
import { RightPanelDialogService } from '../../../@core/utils/right-panel-dialog.service';

// Register French locale
registerLocaleData(localeFr);

@Component({
  selector: "app-asset-form-drawer",
  templateUrl: "./asset-form-drawer.component.html",
  styleUrls: ["./asset-form-drawer.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSpinnerModule,
    NbButtonModule,
    NbPopoverModule,
    FontAwesomeModule,
    NbInputModule,
    TranslateModule,
    NbSelectModule,
    NbOptionModule,
    DescriptifTabComponent,
    SummaryTabComponent,
    MaintenanceTabComponent,
    ComplianceTabComponent,
    DocumentsTabComponent,
    CustomToggleComponent,
    InvestmentListDrawerComponent,
  ],
  providers: [DatePipe, { provide: LOCALE_ID, useValue: "fr-FR" }]
})
export class AssetFormDrawerComponent implements OnInit, OnChanges, OnDestroy {
  // interactions
  @Input() public asset: KPI.MappedAsset;
  @Output() public drawerClosed = new EventEmitter<void>();
  @Output() public toggleChanged = new EventEmitter<{type: 'maintenance' | 'compliance', value: boolean}>();

  // dependencies
  protected translate = inject(TranslateService);
  private assetsService = inject(AssetsService);
  private assetsChanges = inject(AssetsChangesService);
  private configService = inject(ConfigService);
  private settingsService = inject(SettingsService);
  private usersService = inject(UsersService);
  private dialogService = inject(NbDialogService);
  private investmentsService = inject(InvestmentsService);
  private cdr = inject(ChangeDetectorRef);
  private stateLevelService = inject(StateLevelsService);
  private datePipe = inject(DatePipe);
  private scopeService = inject(ScopeService);
  private toastrService = inject(NbToastrService);
  private api = inject(APIService);
  private maintenanceService = inject(MaintenanceService);
  private documentTypesService = inject(DocumentTypesService);
  private documentsService = inject(DocumentsService);
  private saveToast = inject(SaveToastService);
  private rightPanelDialogService = inject(RightPanelDialogService);
  private ref: NbDialogRef<AssetFormDrawerComponent> = inject(NbDialogRef);

  private isSheetDialogOpen: boolean = false;
  public investmentDrawerVisible: boolean = false;
  public investmentIds: number[] = [];
  public investmentData: any[] = [];
  public showDocumentsPanel = false;
  public docCount = 0;

  public title = "";
  public yearsRange: number[] = R.range(1900, new Date().getFullYear() + 1);
  public canGoToAudit: boolean;
  public loading: boolean = false;
  public isButtonLoading: boolean = false;
  public form: UntypedFormGroup;
  public currentForm: UntypedFormGroup;
  public initialValues: any;
  public currentInitialValues: any;
  public activeTab: string = "descriptif";
  public isFullScreen: boolean = false;
  private autoSaveTimer: any = null;
  public isConfirmationModalOpen: boolean = false;

  public shouldShowSaveButton(): boolean {
    const hasForm = !!this.currentForm;
    const hasInitialValues = !!this.currentInitialValues;
    const hasChanges = this.anyFieldValueChanged();
    const isNotAutoSave = !this.shouldAutoSave();
    const noModalOpen = !this.isConfirmationModalOpen;
    const isNotLoading = !this.isButtonLoading;
    return hasForm && hasInitialValues && hasChanges && isNotAutoSave && noModalOpen && isNotLoading;
  }

  public onSave(): void {
    this.isButtonLoading = true;
    this.cdr.detectChanges();
    this.onSubmit(true);
  }

  public onSaveAndClose(): void {
    // Save first, then close when the save completes.
    // Call onSubmit with silent=false so the normal completion path
    // will emit the drawerClosed event after successful save.
    this.onSubmit(false);
  }

  @ViewChild(DescriptifTabComponent) descriptifTabComponent: DescriptifTabComponent;
  @ViewChild(SummaryTabComponent) summaryTabComponent: SummaryTabComponent;
  @ViewChild(MaintenanceTabComponent) maintenanceTabComponent: MaintenanceTabComponent;
  @ViewChild(ComplianceTabComponent) complianceTabComponent: ComplianceTabComponent;
  @ViewChild(DocumentsTabComponent) documentsTabComponent: DocumentsTabComponent;

  public maintenanceAlerts: number = 0;

  public getMaintenanceAlertsCount(): number {
    return this.getMaintenanceActualAlertsCount();
  }

  public getMaintenanceMissingDataCount(): number {
    if (!this.maintenanceManagementValue) {
      return 0;
    }

    if (this.maintenanceTabComponent && this.maintenanceTabComponent.getMissingDataCount) {
      return this.maintenanceTabComponent.getMissingDataCount();
    }

    const maintenance = (this.asset as any).maintenance;
    if (!maintenance) {
      return 0;
    }

    let missingCount = 0;

    const requiredFields = [
      'has_contract',
      'company',
      'level',
      'responsibility',
      'control_frequency',
      'contract_deadline',
      'last_control_date'
    ];

    requiredFields.forEach(field => {
      const value = maintenance[field];

      if (field === 'has_contract') {
        if (value === undefined || value === null) {
          missingCount++;
        }
      } else if (field === 'contract_deadline' || field === 'last_control_date') {
        if (!value || value === null) {
          missingCount++;
        }
      } else {
        if (!value || value === '' || value === null || value === undefined) {
          missingCount++;
        }
      }
    });

    return missingCount;
  }

  private getMaintenanceActualAlertsCount(): number {
    const oldMaintenanceAlerts = (this.asset as any).maintenance_alert_count || 0;
    return oldMaintenanceAlerts;
  }

  private calculateInitialMaintenanceAlerts(): void {
    if (!this.maintenanceManagementValue) {
      this.maintenanceAlerts = this.getMaintenanceActualAlertsCount();
      return;
    }

    let alertCount = 0;

    if ((this.asset as any).maintenance) {
      const maintenance = (this.asset as any).maintenance;

      const fieldsToCheck = [
        { field: 'equipment_evaluation', value: maintenance.equipment_evaluation },
        { field: 'has_contract', value: maintenance.has_contract },
        { field: 'company', value: maintenance.company },
        { field: 'level', value: maintenance.level },
        { field: 'contract_deadline', value: maintenance.contract_deadline },
        { field: 'responsibility', value: maintenance.responsibility },
        { field: 'manufacturer_control_frequency', value: maintenance.manufacturer_control_frequency },
        { field: 'last_control_date', value: maintenance.last_control_date }
      ];

      fieldsToCheck.forEach(({ field, value }) => {
        let isEmpty = false;
        switch (field) {
          case 'has_contract':
            isEmpty = (value === null || value === undefined);
            break;
          case 'equipment_evaluation':
          case 'company':
          case 'level':
          case 'contract_deadline':
          case 'responsibility':
          case 'manufacturer_control_frequency':
          case 'last_control_date':
            isEmpty = (!value || value === '' || value === null);
            break;
        }
        if (isEmpty) {
          alertCount++;
        }
      });
    }

    const oldMaintenanceAlerts = (this.asset as any).maintenance_alert_count || 0;

    this.maintenanceAlerts = alertCount + oldMaintenanceAlerts;
  }

  private updateMaintenanceAlerts(): void {
    if (!this.form) {
      this.maintenanceAlerts = 0;
      return;
    }

    let alertCount = 0;

    if ((this.asset as any).maintenance) {
      const maintenanceFields = [
        'maintenance_evaluation',
        'maintenance_contract',
        'maintenance_company',
        'maintenance_level',
        'maintenance_contract_end_date',
        'maintenance_responsible',
        'manufacturer_control_frequency',
        'last_maintainer_control_date'
      ];

      maintenanceFields.forEach(fieldName => {
        if (fieldName === 'maintenance_evaluation' && !this.assetMaintenanceEnabled) {
          return;
        }

        const control = this.form.get(fieldName);

        if (control) {
          const value = control.value;

          let isEmpty = false;
          switch (fieldName) {
            case 'maintenance_contract_end_date':
            case 'last_maintainer_control_date':
              isEmpty = (!value || value === null);
              break;
            case 'maintenance_evaluation':
            case 'maintenance_contract':
            case 'maintenance_company':
            case 'maintenance_level':
            case 'maintenance_responsible':
            case 'manufacturer_control_frequency':
              isEmpty = (!value || value === '' || value === null);
              break;
          }

          if (isEmpty) {
            alertCount++;
          }
        }
      });
    }

    const oldMaintenanceAlerts = (this.asset as any).maintenance_alert_count || 0;
    this.maintenanceAlerts = alertCount + oldMaintenanceAlerts;
  }
  public assetInvestmentsTotal: number = 0;
  public assetTypeHierarchy: string = "";
  public perimeterHierarchy: string = "";
  public assetSiteName: string = "";
  public technicalStateLabel: string = "";
  public technicalStatusEvaluatedBy: string = "";
  public technicalStatusEvaluationDate: string = "";
  public reserves: Array<{ date_emission: string; libelle: string }> = [];
  public maintenanceManagementEnabled: boolean = false;
  public complianceManagementEnabled: boolean = false;
  public documentTabEnabled: boolean = false;
  public maintenanceManagementValue: boolean = false;
  public complianceManagementValue: boolean = false;
  private maintenanceToggleDebounceTimer: any = null;
  private complianceToggleDebounceTimer: any = null;
  public iniesDataSheets: string[] = [];
  public showIniesModal: boolean = false;
  public showMaintenanceContractIndicator: boolean = false;
  public customerConfig: KPI.CustomerConfig;
  public assetMaintenanceEnabled: boolean = false;
  private assetComplianceEnabled: boolean = false;
  public assetEquipmentValidation: boolean = false;
  private assetEquipmentAndReserveXlsExport: boolean = false;

  private subscriptions: Subscription[] = [];
  private assetSubject = new ReplaySubject<any>(1);
  protected NUMBER_VALIDATORS = [
    Validators.pattern(/^-?\d+\.?\d*$/),
    Validators.pattern(/^(?!-0(\.0+)?$)-?(0|[1-9]\d*)(\.\d+)?$/)
  ];

  ngOnInit(): void {
    this.assetInvestmentsTotal = (this.asset.investments?.length || 0) + (this.asset.global_investments?.length || 0);
    this.subscriptions.push(
      combineLatest([
        this.assetSubject.pipe(filter(asset => !!asset)),
        this.configService.customerConfig$.pipe(filter(config => !!config)),
        this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_ACCESS_AUDIT]),
        this.settingsService.assetMaintenanceManagement$,
        this.settingsService.assetConformityManagement$,
        this.settingsService.assetEquipmentValidation$
      ]).pipe(
        take(1)
      ).subscribe(([asset, config, accessAllowed, globalMaintenanceEnabled, globalComplianceEnabled, equipmentValidation]) => {
        this.customerConfig = config;
        this.canGoToAudit = accessAllowed && config.can_access_audit_app;

        this.assetMaintenanceEnabled = globalMaintenanceEnabled;
        this.assetComplianceEnabled = globalComplianceEnabled;
        this.assetEquipmentValidation = equipmentValidation;
        this.assetEquipmentAndReserveXlsExport = config.equipment_and_reserve_xls_export;

        const assetTypeMaintenanceEnabled = (asset?.asset_type_object as any)?.maintenance_management || false;
        const assetTypeComplianceEnabled = (asset?.asset_type_object as any)?.compliance_management || false;

        this.maintenanceManagementEnabled = globalMaintenanceEnabled && assetTypeMaintenanceEnabled;
        this.complianceManagementEnabled = globalComplianceEnabled && assetTypeComplianceEnabled;
        this.documentTabEnabled = this.settingsService.settingMustBeTrue('DOCUMENT_TAB_IN_EQUIPMENT_SHEET');
        this.updateDocumentAlerts();

        // If user was on old separate tabs, redirect to merged tab
        if (this.activeTab === "maintenance" || this.activeTab === "conformite") {
          this.activeTab = "conformite-maintenance";
        }
        // If merged tab is not available, fallback to descriptif
        if (this.activeTab === "conformite-maintenance" && !this.maintenanceManagementEnabled && !this.complianceManagementEnabled) {
          this.activeTab = "descriptif";
        }

        this.cdr.detectChanges();
      })
    );

    this.title = this.translate.instant("Asset-sheet") + " (" + this.translate.instant("quick change") + ")";

    setTimeout(() => {
      if (!this.maintenanceManagementEnabled && !this.complianceManagementEnabled) {
        this.configService.customerConfig$.pipe(
          filter(config => !!config),
          take(1)
        ).subscribe(config => {
          this.evaluateTabVisibilityWithConfig(config);
        });
      }
    }, 2000);

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["asset"] && this.asset) {
      this.assetSubject.next(this.asset);
      this.maintenanceManagementValue = (this.asset as any).maintenance_management || false;
      this.complianceManagementValue = (this.asset as any).compliance_management || false;
      this.saveToast.close();
      this.initializeForm();
      this.buildAssetTypeHierarchy();
      this.buildPerimeterHierarchy();
      this.assetSiteName = this.asset?.perimeters?.[0]?.name ?? "";
      this.loadTechnicalState();
      this.loadTechnicalStatusEvaluation();
      this.loadAssetTypeDetails();
      this.setMaintenanceContractIndicator();
      this.loadDocumentCount();
      this.subscriptions.push(
        this.documentsService._documentsHaveChanged$.subscribe(() => {
          this.loadDocumentCount();
        })
      );
    }
  }

  private loadTechnicalState(): void {
    this.subscriptions.push(
      this.assetsService.getAssetLifecycle(this.asset.id).subscribe(lifecycle => {
        const lastAuditPoint = lifecycle.audit_points[lifecycle.audit_points.length - 1];
        const ratingReasons = lastAuditPoint?.rating_reasons?.map(r => r.name).join(", ") || "-";
        const currentYear = new Date().getFullYear();
        const remainingYears = lifecycle.planned_eol - currentYear;

        const hasRealAudit = lifecycle.audit_points && lifecycle.audit_points.length > 0;
        if (hasRealAudit) {
          const note = lifecycle.current_year_note;
          this.technicalStateLabel = this.stateLevelService.getTechnicalStateLabelFromNote(note);
        } else {
          this.technicalStateLabel = "";
        }

        this.form?.patchValue({
          etat_technique: this.technicalStateLabel,
          motif_evaluation: ratingReasons,
          nb_annees_restantes: remainingYears
        });
        this.initialValues.etat_technique = this.technicalStateLabel;
        this.initialValues.motif_evaluation = ratingReasons;
        this.initialValues.nb_annees_restantes = remainingYears;
        this.updateSummaryAlerts();
        this.cdr.detectChanges();
      })
    );
  }

  private loadTechnicalStatusEvaluation(): void {
    this.technicalStatusEvaluatedBy = (this.asset as any).technical_status_evaluated_by || "";
    this.technicalStatusEvaluationDate = (this.asset as any).technical_status_evaluation_date || "";
  }

  private loadAssetTypeDetails(): void {
    this.subscriptions.push(
      this.scopeService.assetTypes$.pipe(first()).subscribe(assetTypes => {
        const assetTypeDetails = assetTypes[this.asset.asset_type];
        if (assetTypeDetails?.inies_environmental_data_sheet) {
          const iniesCount = assetTypeDetails.inies_environmental_data_sheet.length;
          this.iniesDataSheets = assetTypeDetails.inies_environmental_data_sheet;
          if (this.form?.get("fiche_iniies")) {
            this.form.get("fiche_iniies").setValue(iniesCount);
            this.initialValues.fiche_iniies = iniesCount;
          }
        }
      })
    );
  }

  private setMaintenanceContractIndicator(): void {
    const maintenance = (this.asset as any).maintenance;
    this.showMaintenanceContractIndicator = !maintenance || maintenance.has_contract === false;
  }

  private evaluateTabVisibilityWithConfig(_config: any): void {
    if (!this.asset) {
      return;
    }

    const globalMaintenanceEnabled = this.assetMaintenanceEnabled;
    const globalComplianceEnabled = this.assetComplianceEnabled;
    const assetTypeMaintenanceEnabled = (this.asset?.asset_type_object as any)?.maintenance_management || false;
    const assetTypeComplianceEnabled = (this.asset?.asset_type_object as any)?.compliance_management || false;

    this.maintenanceManagementEnabled = globalMaintenanceEnabled && assetTypeMaintenanceEnabled;
    this.complianceManagementEnabled = globalComplianceEnabled && assetTypeComplianceEnabled;
    this.documentTabEnabled = this.settingsService.settingMustBeTrue('DOCUMENT_TAB_IN_EQUIPMENT_SHEET');
    this.cdr.detectChanges();
  }

  public conformiteAlerts: number = 0;

  public getConformiteAlertsCount(): number {
    if (!this.complianceManagementValue) {
      const reservationsCount = (this.asset as any).compliance?.reservations_count || 0;
      return reservationsCount > 0 ? 1 : 0;
    }
    const oldComplianceAlerts = (this.asset as any).compliance_alert_count || 0;
    return oldComplianceAlerts;
  }

  public getComplianceMissingDataCount(): number {
    if (!this.complianceManagementValue) {
      return 0;
    }

    if (this.complianceTabComponent && this.complianceTabComponent.getMissingDataCount) {
      return this.complianceTabComponent.getMissingDataCount();
    }

    const compliance = (this.asset as any).compliance;
    if (!compliance) {
      return 0;
    }

    let missingCount = 0;

    const requiredFields = [
      'control_frequency',
      'environmental_compliance',
      'last_control_date'
    ];

    requiredFields.forEach(field => {
      const value = compliance[field];

      if (field === 'environmental_compliance') {
        if (value === undefined || value === null) {
          missingCount++;
        }
      } else if (field === 'last_control_date') {
        if (!value || value === null) {
          missingCount++;
        }
      } else {
        if (!value || value === '' || value === null || value === undefined) {
          missingCount++;
        }
      }
    });

    return missingCount;
  }

  public getCombinedMissingDataCount(): number {
    let totalCount = 0;

    if (this.maintenanceManagementEnabled && this.maintenanceManagementValue) {
      totalCount += this.getMaintenanceMissingDataCount();
    }

    if (this.complianceManagementEnabled && this.complianceManagementValue) {
      totalCount += this.getComplianceMissingDataCount();
    }

    return totalCount;
  }

  public getTotalMissingDataCount(): number {
    return this.descriptifAlerts + this.summaryAlerts + this.getCombinedMissingDataCount();
  }

  private getComplianceActualAlertsCount(): number {
    const oldComplianceAlerts = (this.asset as any).compliance_alert_count || 0;
    return oldComplianceAlerts;
  }

  private calculateInitialComplianceAlerts(): void {
    if (!this.complianceManagementValue) {
      this.conformiteAlerts = this.getComplianceActualAlertsCount();
      return;
    }

    let alertCount = 0;

    if ((this.asset as any).compliance) {
      const compliance = (this.asset as any).compliance;

      const fieldsToCheck = [
        { field: 'control_frequency', value: compliance.control_frequency },
        { field: 'last_control_date', value: compliance.last_control_date },
        { field: 'environmental_compliance', value: compliance.environmental_compliance }
      ];

      fieldsToCheck.forEach(({ field, value }) => {
        let isEmpty = false;
        switch (field) {
          case 'environmental_compliance':
            isEmpty = (value === null || value === undefined);
            break;
          case 'control_frequency':
            isEmpty = (!value || value === '' || value === null);
            break;
          case 'last_control_date':
            isEmpty = (!value || value === null);
            break;
        }
        if (isEmpty) {
          alertCount++;
        }
      });
    }

    const oldComplianceAlerts = (this.asset as any).compliance_alert_count || 0;
    this.conformiteAlerts = alertCount + oldComplianceAlerts;
  }

  private updateComplianceAlerts(): void {
    if (!this.form) {
      this.conformiteAlerts = 0;
      return;
    }

    let alertCount = 0;

    if ((this.asset as any).compliance) {
      const complianceFields = [
        'control_frequency',
        'last_control_date',
        'environmental_compliance'
      ];

      complianceFields.forEach(fieldName => {
        const control = this.form.get(fieldName);

        if (control) {
          const value = control.value;

          let isEmpty = false;
          switch (fieldName) {
            case 'last_control_date':
              isEmpty = (!value || value === null);
              break;
            case 'control_frequency':
            case 'environmental_compliance':
              isEmpty = (!value || value === '' || value === null);
              break;
          }
          if (isEmpty) {
            alertCount++;
          }
        }
      });
    }

    const oldComplianceAlerts = (this.asset as any).compliance_alert_count || 0;

    this.conformiteAlerts = alertCount + oldComplianceAlerts;
  }

  public descriptifAlerts: number = 0;
  public summaryAlerts: number = 0;
  public missingMandatoryDocuments: number = 0;

  private updateDocumentAlerts(): void {
    if (!this.asset?.id || !this.asset?.asset_type || !this.documentTabEnabled) return;
    combineLatest([
      this.documentTypesService.documentTypes$.pipe(first()),
      this.api.documents_document_type_relationships(
        { app_label: "assets", model: "assettype", object_id: this.asset.asset_type },
        true,
      ),
      this.api.documents_documents({ assets: this.asset.id }),
    ]).pipe(first()).subscribe({
      next: ([allDocTypes, relationships, documents]) => {
        const docTypeMap = new Map(allDocTypes.map((dt) => [dt.id, dt]));
        const mandatoryIds = new Set(
          relationships
            .filter((rel) => docTypeMap.has(rel.document_type) && (rel.values?.mandatory ?? false))
            .map((rel) => rel.document_type)
        );
        this.missingMandatoryDocuments = [...mandatoryIds].filter(
          (dtId) => !documents.some((d) => d.type === docTypeMap.get(dtId)!.label)
        ).length;
        this.cdr.detectChanges();
      },
    });
  }

  private updateDescriptifAlerts(): void {
    this.descriptifAlerts = 0;
  }

  private updateSummaryAlerts(): void {
    if (!this.asset) {
      this.summaryAlerts = 0;
      return;
    }

    this.summaryAlerts = (this.asset as any).total_missing_lifecycle || 0;
  }

  public isRegulatoryControlOverdue(): boolean {
    // const controleRequired = this.form?.get("controle_reglementaire")?.value;
    const complianceManagement = (this.asset as any)?.compliance_management;
    const frequency = this.form?.get("frequence_controle")?.value;
    const lastControlDate = this.form?.get("date_dernier_controle")?.value;

    // Use compliance_management toggle instead of controle_reglementaire field
    if (!complianceManagement || !frequency || !lastControlDate || frequency === "-") {
      return false;
    }

    const lastControl = new Date(lastControlDate);
    const today = new Date();

    if (isNaN(lastControl.getTime())) {
      return false;
    }

    const diffTime = today.getTime() - lastControl.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    const diffYears = diffDays / 365.25;

    switch (frequency) {
      case "annuelle":
      case "1 fois par an":
        return diffYears >= 1;
      case "1 fois tous les 2 ans":
        return diffYears >= 2;
      case "1 fois tous les 3 ans":
        return diffYears >= 3;
      case "1 fois tous les 5 ans":
        return diffYears >= 5;
      case "1 fois tous les 10 ans":
        return diffYears >= 10;
      default:
        return false;
    }
  }

  public isMaintenanceAlertRed(): boolean {
    return (this.asset as any).maintenance && (this.asset as any).maintenance.alert_color === "red";
  }

  public isComplianceAlertRed(): boolean {
    return (this.asset as any).compliance && (this.asset as any).compliance.alert_color === "red";
  }

  public isComplianceAlertOrange(): boolean {
    return (this.asset as any).compliance && (this.asset as any).compliance.alert_color === "orange";
  }

  public getMaintenanceContractColor(): string {
    const maintenance = (this.asset as any).maintenance;
    return getColorHex(maintenance?.has_contract_color);
  }

  public getMaintenanceContractDeadlineColor(): string {
    const maintenance = (this.asset as any).maintenance;
    return getColorHex(maintenance?.contract_deadline_color);
  }

  public getMaintenanceLastControlDateColor(): string {
    const maintenance = (this.asset as any).maintenance;
    return getColorHex(maintenance?.last_control_date_color);
  }

  public getComplianceLastControlDateColor(): string {
    const compliance = (this.asset as any).compliance;
    return getColorHex(compliance?.last_control_date_color);
  }

  public getComplianceReservationsColor(): string {
    const compliance = (this.asset as any).compliance;
    return getColorHex(compliance?.reservations_color);
  }

  public getComplianceEnvironmentalComplianceColor(): string {
    const compliance = (this.asset as any).compliance;
    return getColorHex(compliance?.environmental_compliance_color);
  }

  public onDescriptifFormChange(event: { form: UntypedFormGroup; initialValues: any }): void {
    this.currentForm = event.form;
    this.currentInitialValues = event.initialValues;
    this.form = event.form;
    this.updateDescriptifAlerts();
  }

  public onDescriptifSaveRequested(): void {
    if (this.anyFieldValueChanged() && this.isFormValid()) {
      this.setSaving();
      this.handleDescriptifSubmission();
    }
  }

  public onDescriptifAssetDataChanged(): void {
    this.updateDescriptifAlerts();
  }

  public onSummaryFormChange(event: { form: UntypedFormGroup; initialValues: any }): void {
    this.currentForm = event.form;
    this.currentInitialValues = event.initialValues;
    this.form = event.form;
    this.updateSummaryAlerts();

    if (this.shouldAutoSave()) {
      this.triggerAutoSave();
    }
  }

  public onSummaryAssetDataChanged(): void {
    this.updateSummaryAlerts();
  }

  public onConfirmationModalStateChanged(isOpen: boolean): void {
    this.isConfirmationModalOpen = isOpen;
    this.cdr.detectChanges();
  }

  public onSummaryRequestSave(): void {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  public onMaintenanceFormChange(event: { form: UntypedFormGroup; initialValues: any }): void {
    const isInitialSetup = this.currentForm !== event.form;

    this.currentForm = event.form;
    this.currentInitialValues = event.initialValues;
    this.form = event.form;
    this.updateMaintenanceAlerts();
    this.cdr.detectChanges();

    if (!isInitialSetup && this.shouldAutoSave()) {
      this.triggerAutoSave();
    }
  }

  public onComplianceFormChange(event: { form: UntypedFormGroup; initialValues: any }): void {
    const isInitialSetup = this.currentForm !== event.form;

    this.currentForm = event.form;
    this.currentInitialValues = event.initialValues;
    this.form = event.form;
    this.updateComplianceAlerts();
    this.cdr.detectChanges();

    // Trigger auto-save when field values change (but not on initial setup)
    if (!isInitialSetup && this.shouldAutoSave()) {
      this.triggerAutoSave();
    }
  }

  public onMaintenanceManagementChange(value: boolean): void {
    if (this.maintenanceToggleDebounceTimer) {
      clearTimeout(this.maintenanceToggleDebounceTimer);
    }

    this.maintenanceManagementValue = value;
    this.cdr.detectChanges();

    if (this.form && this.form.get('maintenance_management')) {
      this.form.get('maintenance_management').setValue(value);
    }

    this.calculateInitialMaintenanceAlerts();

    this.toggleChanged.emit({type: 'maintenance', value});

    this.maintenanceToggleDebounceTimer = setTimeout(() => {
      this.callMaintenanceToggleAPI(value);
    }, 300);
  }

  private callMaintenanceToggleAPI(value: boolean): void {
    this.api.toggle_asset_maintenance(this.asset.id).subscribe({
      next: (_response) => {
        (this.asset as any).maintenance_management = value;
        this.assetsChanges.pendingChangesNumber$.next(1);
        setTimeout(() => {
          this.assetsChanges.pendingChangesNumber$.next(0);
          this.refreshAssetDataInternal(true).subscribe();
        }, 100);
      },
      error: (error) => {
        console.error('Error toggling maintenance management:', error);
        this.maintenanceManagementValue = !value;
        this.cdr.detectChanges();
      }
    });
  }

  public onComplianceManagementChange(value: boolean): void {
    if (this.complianceToggleDebounceTimer) {
      clearTimeout(this.complianceToggleDebounceTimer);
    }

    this.complianceManagementValue = value;
    this.cdr.detectChanges();

    if (this.form && this.form.get('compliance_management')) {
      this.form.get('compliance_management').setValue(value);
    }

    this.calculateInitialComplianceAlerts();

    // Emit toggle change event
    this.toggleChanged.emit({type: 'compliance', value});

    this.complianceToggleDebounceTimer = setTimeout(() => {
      this.callComplianceToggleAPI(value);
    }, 300);
  }

  private callComplianceToggleAPI(value: boolean): void {
    this.api.toggle_asset_compliance(this.asset.id).subscribe({
      next: (_response) => {
        (this.asset as any).compliance_management = value;
        this.assetsChanges.pendingChangesNumber$.next(1);
        setTimeout(() => {
          this.assetsChanges.pendingChangesNumber$.next(0);
          this.refreshAssetDataInternal(true).subscribe();
        }, 100);
      },
      error: (error) => {
        console.error('Error toggling compliance management:', error);
        this.complianceManagementValue = !value;
        this.cdr.detectChanges();
      }
    });
  }

  public refreshAssetData(): void {
    this.refreshAssetDataInternal(false).subscribe();
  }

  // internal refresh with optional force flag
  private refreshAssetDataInternal(force: boolean): Observable<any> {
    if (!this.asset?.id) {
      return of(null);
    }

    // If there are unsaved local changes, avoid refreshing from server unless forced.
    if (!force && this.anyFieldValueChanged()) {
      return of(null);
    }

    return this.assetsService.getAsset(this.asset.id).pipe(
      first(),
      tap((updatedAsset: any) => {
        this.asset = { ...updatedAsset as unknown as KPI.MappedAsset };
        this.updateDescriptifAlerts();
        this.updateSummaryAlerts();
        this.cdr.detectChanges();
      }),
      catchError((error) => {
        console.error('Error refreshing asset data:', error);
        return of(null);
      })
    );
  }

  public addReserve(): void {
    const today = new Date();
    this.reserves.push({
      date_emission: this.datePipe.transform(today, "y-MM-dd"),
      libelle: "Ecrire votre texte ici"
    });
    this.updateReservesCount();
    this.cdr.detectChanges();
  }

  public deleteReserve(index: number): void {
    this.reserves.splice(index, 1);
    this.updateReservesCount();
    this.cdr.detectChanges();
  }

  public updateReservesCount(): void {
    this.form.get("reserves_en_cours").setValue(this.reserves.length);
  }

  public isReserveOlderThan3Months(dateString: string): boolean {
    if (!dateString) return false;
    const reserveDate = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - reserveDate.getTime());
    const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30);
    return diffMonths > 3;
  }

  public hasOldReserves(): boolean {
    return this.reserves.some(reserve => this.isReserveOlderThan3Months(reserve.date_emission));
  }

  public formatDisplayDate(dateString: string): string {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return this.datePipe.transform(date, "d MMM. y", "fr-FR");
  }

  private buildPerimeterHierarchy(): void {
    if (this.asset?.perimeters) {
      const site = this.asset.perimeters[0];
      const lastPerimeter = this.asset.perimeters[this.asset.perimeters.length - 1];

      if (lastPerimeter.level_parent && this.asset.perimeters.length === 2) {
        const level1Perimeter = site.sub_perimeters?.find(sp => sp.id === lastPerimeter.level_parent);
        if (level1Perimeter) {
          this.perimeterHierarchy = [site.name, level1Perimeter.name].join(" > ") + " > ";
        } else {
          this.perimeterHierarchy = this.asset.perimeters.map(perimeter => perimeter.name).join(" > ") + " > ";
        }
      } else {
        this.perimeterHierarchy = this.asset.perimeters.map(perimeter => perimeter.name).join(" > ") + " > ";
      }
    }
  }

  private buildAssetTypeHierarchy(): void {
    if (this.asset) {
      const parts = [];
      if (this.asset.super_category?.name) parts.push(this.asset.super_category.name);
      if (this.asset.category?.name) parts.push(this.asset.category.name);
      if (this.asset.sub_category?.name) parts.push(this.asset.sub_category.name);
      if (this.asset.asset_type_object?.name) parts.push(this.asset.asset_type_object.name);

      this.assetTypeHierarchy = parts.join(" > ");
    }
  }

  private initializeForm(): void {
    this.initialValues = {
      label: this.asset.label || "",
      installation_year: this.asset.installation_year || null,
      quantity: this.asset.quantity || null,
      comments: this.asset.comments || "",
      duree_vie_reference: this.asset.asset_type_object?.expected_duration || null,
      fiche_iniies: this.asset.asset_type_object?.inies_environmental_data_sheet?.length || null,
      code_uniformat: (this.asset as any).uniformat_code || "",
      floor: this.asset.floor || null,
      etat_technique: this.technicalStateLabel || "",
      motif_evaluation: "",
      nb_annees_restantes: null,
      brand: this.asset.brand || "",
      model: this.asset.model || "",
      maintenance_evaluation: (this.asset as any).maintenance?.equipment_evaluation || "",
      maintenance_contract: (this.asset as any).maintenance?.has_contract ? "true" : "false",
      maintenance_company: (this.asset as any).maintenance?.company || "",
      maintenance_level: (this.asset as any).maintenance?.level?.toString() || "",
      maintenance_contract_end_date: (this.asset as any).maintenance?.contract_deadline || null,
      maintenance_responsible: (this.asset as any).maintenance?.responsibility || "",
      manufacturer_control_frequency: (this.asset as any).maintenance?.control_frequency || "",
      last_maintainer_control_date: (this.asset as any).maintenance?.last_control_date || null,
      maintenance_comment: (this.asset as any).maintenance?.comment || "",
      maintenance_management: (this.asset as any).maintenance_management || false,
      compliance_management: (this.asset as any).compliance_management || false,
      // controle_reglementaire: (this.asset as any).compliance?.control_required ? "true" : "false",
      frequence_controle: (this.asset as any).compliance?.control_frequency || "",
      description_controle: (this.asset as any).compliance?.control_description || "",
      date_dernier_controle: (this.asset as any).compliance?.last_control_date || null,
      reserves_en_cours: (this.asset as any).compliance?.reservations_count || 0,
      conformite_environnementale: (this.asset as any).compliance?.environmental_compliance !== undefined
        ? (this.asset as any).compliance.environmental_compliance
          ? "true"
          : "false"
        : ""
    };

    this.reserves = [];
    if ((this.asset as any).compliance?.reservations) {
      this.reserves = (this.asset as any).compliance.reservations.map((reservation: any) => ({
        date_emission: reservation.issue_date,
        libelle: reservation.label
      }));
    }

    this.form = new UntypedFormGroup({
      label: new UntypedFormControl({ value: this.initialValues.label, disabled: false }, [
        Validators.required,
        Validators.maxLength(255)
      ]),
      installation_year: new UntypedFormControl({ value: this.initialValues.installation_year, disabled: false }, [
        Validators.required
      ]),
      quantity: new UntypedFormControl({ value: this.initialValues.quantity, disabled: false }, [
        Validators.required,
        Validators.min(1),
        ...this.NUMBER_VALIDATORS
      ]),
      comments: new UntypedFormControl({ value: this.initialValues.comments, disabled: false }),
      duree_vie_reference: new UntypedFormControl({ value: this.initialValues.duree_vie_reference, disabled: false }, [
        Validators.required,
        Validators.min(1),
        ...this.NUMBER_VALIDATORS
      ]),
      fiche_iniies: new UntypedFormControl({ value: this.initialValues.fiche_iniies, disabled: false }, [
        Validators.required,
        Validators.min(1),
        ...this.NUMBER_VALIDATORS
      ]),
      code_uniformat: new UntypedFormControl({ value: this.initialValues.code_uniformat, disabled: false }),
      floor: new UntypedFormControl({ value: this.initialValues.floor, disabled: false }),
      etat_technique: new UntypedFormControl({ value: this.initialValues.etat_technique, disabled: false }),
      motif_evaluation: new UntypedFormControl({ value: this.initialValues.motif_evaluation, disabled: false }),
      nb_annees_restantes: new UntypedFormControl({ value: this.initialValues.nb_annees_restantes, disabled: false }, [
        Validators.required,
        Validators.min(0),
        ...this.NUMBER_VALIDATORS
      ]),
      brand: new UntypedFormControl({ value: this.initialValues.brand, disabled: false }),
      model: new UntypedFormControl({ value: this.initialValues.model, disabled: false }),
      maintenance_evaluation: new UntypedFormControl({
        value: this.initialValues.maintenance_evaluation,
        disabled: false
      }),
      maintenance_contract: new UntypedFormControl({ value: this.initialValues.maintenance_contract, disabled: false }),
      maintenance_company: new UntypedFormControl({ value: this.initialValues.maintenance_company, disabled: false }),
      maintenance_level: new UntypedFormControl({ value: this.initialValues.maintenance_level, disabled: false }),
      maintenance_contract_end_date: new UntypedFormControl({
        value: this.initialValues.maintenance_contract_end_date,
        disabled: false
      }),
      maintenance_responsible: new UntypedFormControl({
        value: this.initialValues.maintenance_responsible,
        disabled: false
      }),
      manufacturer_control_frequency: new UntypedFormControl({
        value: this.initialValues.manufacturer_control_frequency,
        disabled: false
      }),
      last_maintainer_control_date: new UntypedFormControl({
        value: this.initialValues.last_maintainer_control_date,
        disabled: false
      }),
      maintenance_comment: new UntypedFormControl({ value: this.initialValues.maintenance_comment, disabled: false }),
      maintenance_management: new UntypedFormControl({ value: this.initialValues.maintenance_management, disabled: false }),
      compliance_management: new UntypedFormControl({ value: this.initialValues.compliance_management, disabled: false }),
      // controle_reglementaire: new UntypedFormControl({
      //   value: this.initialValues.controle_reglementaire,
      //   disabled: false
      // }),
      frequence_controle: new UntypedFormControl({ value: this.initialValues.frequence_controle, disabled: false }),
      description_controle: new UntypedFormControl({ value: this.initialValues.description_controle, disabled: false }),
      date_dernier_controle: new UntypedFormControl({
        value: this.initialValues.date_dernier_controle,
        disabled: false
      }),
      reserves_en_cours: new UntypedFormControl({ value: this.initialValues.reserves_en_cours, disabled: false }),
      conformite_environnementale: new UntypedFormControl({
        value: this.initialValues.conformite_environnementale,
        disabled: false
      })
    });

    this.subscriptions.push(
      // this.form.get("controle_reglementaire").valueChanges.subscribe(() => this.cdr.detectChanges()),
      this.form.get("frequence_controle").valueChanges.subscribe(() => this.cdr.detectChanges()),
      this.form.get("date_dernier_controle").valueChanges.subscribe(() => this.cdr.detectChanges())
    );

    setTimeout(() => {
      if (this.form.get('maintenance_management')) {
        this.form.get('maintenance_management').setValue((this.asset as any).maintenance_management || false);
      }
      if (this.form.get('compliance_management')) {
        this.form.get('compliance_management').setValue((this.asset as any).compliance_management || false);
      }
    }, 100);
    this.updateDescriptifAlerts();
    this.updateSummaryAlerts();
    this.updateDocumentAlerts();
    this.calculateInitialMaintenanceAlerts();
    this.calculateInitialComplianceAlerts();
  }

  public loadDocumentCount(): void {
    if (!this.asset?.id) return;
    this.subscriptions.push(
      this.documentsService.getDocumentsCount(
        [this.asset.id],
        [],
        [],
        []
      ).subscribe((count) => {
        this.docCount = count;
        this.cdr.detectChanges();
      })
    );
  }

  public toggleDocumentsPanel(): void {
    this.showDocumentsPanel = !this.showDocumentsPanel;
  }

  public onDocumentsDrawerClosed(): void {
    this.showDocumentsPanel = false;
    this.loadDocumentCount();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });

    if (this.maintenanceToggleDebounceTimer) {
      clearTimeout(this.maintenanceToggleDebounceTimer);
    }
    if (this.complianceToggleDebounceTimer) {
      clearTimeout(this.complianceToggleDebounceTimer);
    }
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
  }

  public onSubmit(silent: boolean = false): void {
    if (!silent) {
      this.loading = true;
    }
    this.setSaving();

    if (!this.currentForm || !this.currentInitialValues) {
      this.loading = false;
      this.isButtonLoading = false;
      this.saveToast.close();
      return;
    }

    if (this.activeTab === "conformite-maintenance") {
      this.handleMergedTabSubmission();
      return;
    }

    if (this.activeTab === "maintenance") {
      this.handleMaintenanceSubmission();
      return;
    }

    if (this.activeTab === "conformite") {
      this.handleComplianceSubmission();
      return;
    }

    if (this.activeTab === "descriptif") {
      this.handleDescriptifSubmission();
      return;
    }

    if (this.summaryTabComponent?.isLifetimeBelowThreshold()) {
      this.summaryTabComponent.openLifetimeConsistencyModal();
    }

    const changes: { property: string; value: any }[] = Object.keys(this.currentInitialValues)
      .filter(key => this.fieldValueChanged(key))
      .map((key: string) => ({
        property: key,
        value: this.currentForm.get(key)?.value
      }));

    const ratingReasonsChanged = this.fieldValueChanged('motif_evaluation');
    const technicalStateChanged = this.fieldValueChanged('etat_technique');
    const missingReasonChanged = this.fieldValueChanged('motif_absence_evaluation');
    const remainingYearsChanged = this.fieldValueChanged('nb_annees_restantes');
    const maintenanceEvaluationChanged = this.fieldValueChanged('maintenance_evaluation');

    const hasTechnicalStateOrReasonsChange =
      (ratingReasonsChanged || technicalStateChanged || remainingYearsChanged) &&
      this.summaryTabComponent &&
      this.summaryTabComponent.currentNote !== null &&
      this.summaryTabComponent.currentNote !== undefined;
    const hasMissingNotationChange =
      (technicalStateChanged || missingReasonChanged) &&
      this.summaryTabComponent &&
      this.summaryTabComponent.currentNote === null;

    // Proceed with normal save
    this.proceedWithSubmit(
      changes,
      hasTechnicalStateOrReasonsChange,
      hasMissingNotationChange,
      maintenanceEvaluationChanged,
      silent,
      technicalStateChanged || remainingYearsChanged
    );
  }

  private proceedWithSubmit(
    changes: { property: string; value: any }[],
    hasTechnicalStateOrReasonsChange: boolean,
    hasMissingNotationChange: boolean,
    hasMaintenanceEvaluationChange: boolean = false,
    silent: boolean = false,
    hasLifecycleReplacementChange: boolean = false
  ): void {
    if (!silent) {
      this.loading = true;
    }

    const regularChanges = changes.filter(change =>
      change.property !== 'motif_evaluation' &&
      change.property !== 'etat_technique' &&
      change.property !== 'nb_annees_restantes' &&
      change.property !== 'motif_absence_evaluation' &&
      change.property !== 'notation_missing_comment' &&
      change.property !== 'maintenance_evaluation'
    );

    if (regularChanges.length > 0) {
      this.assetsChanges.addChanges(this.asset.id, regularChanges);
    }

    const saveOperations: Observable<any>[] = [];
    // defaultIfEmpty(null) guards against an operation that completes without emitting
    // (e.g. saveTechnicalStateAndReasons() when the technical-state question is missing):
    // without it, combineLatest below would never fire `next` and the "Saving in progress"
    // toast would stay open forever.
    if (hasTechnicalStateOrReasonsChange) {
      saveOperations.push(this.summaryTabComponent.saveTechnicalStateAndReasons().pipe(defaultIfEmpty(null)));
    } else if (hasMissingNotationChange) {
      saveOperations.push(this.summaryTabComponent.saveMissingNotationReason().pipe(defaultIfEmpty(null)));
    }

    if (hasMaintenanceEvaluationChange) {
      saveOperations.push(this.saveMaintenanceEvaluation().pipe(defaultIfEmpty(null)));
    }

    if (hasLifecycleReplacementChange) {
      this.subscriptions.push(
        this.getAssetInvestments().pipe(
          tap(investments => this.showLifecycleReplacementDialogForInvestments(investments))
        ).subscribe()
      );
    }

    if (saveOperations.length > 0 || regularChanges.length > 0) {
      combineLatest([
        ...saveOperations,
        regularChanges.length > 0
          ? this.assetsChanges.pendingChangesNumber$.pipe(
        filter(nb => !nb),
        first()
      )
          : of(null)
      ]).subscribe({
        next: () => {
          regularChanges.forEach(change => {
            this.asset[change.property] = change.value;
            this.initialValues[change.property] = change.value;
            if (this.currentInitialValues) {
              this.currentInitialValues[change.property] = change.value;
            }
          });

          if (hasTechnicalStateOrReasonsChange) {
            const newRatingReasons = this.currentForm.get('motif_evaluation')?.value || [];
            const newTechnicalState = this.currentForm.get('etat_technique')?.value || '';
            const newRemainingYears = this.currentForm.get('nb_annees_restantes')?.value || 0;

            (this.asset as any).notation_missing_reason = KPI.MissingNotationReason.NOT_SET;
            (this.asset as any).notation_missing_comment = "";

            this.initialValues.motif_evaluation = newRatingReasons;
            this.initialValues.etat_technique = newTechnicalState;
            this.initialValues.nb_annees_restantes = newRemainingYears;

            if (this.currentInitialValues) {
              this.currentInitialValues.motif_evaluation = newRatingReasons;
              this.currentInitialValues.etat_technique = newTechnicalState;
              this.currentInitialValues.nb_annees_restantes = newRemainingYears;
            }

            if (!silent) {
              if (this.summaryTabComponent) {
                this.summaryTabComponent.loadTechnicalState();
              }

              if (this.asset?.id) {
                // Refresh asset from server but do not overwrite unsaved local changes unless forced.
                this.refreshAssetDataInternal(true).subscribe(() => {
                  if (this.summaryTabComponent) {
                    this.summaryTabComponent.loadTechnicalState();
                  }
                });
              }
            }

            this.assetsChanges.pendingChangesNumber$.next(0);
          } else if (hasMissingNotationChange) {
            const newMissingReason = this.currentForm.get('motif_absence_evaluation')?.value || KPI.MissingNotationReason.WAITING_EVALUATION;
            const newMissingComment = this.currentForm.get('notation_missing_comment')?.value || '';
            const newTechnicalState = this.currentForm.get('etat_technique')?.value || '';

            (this.asset as any).notation_missing_reason = newMissingReason;
            (this.asset as any).notation_missing_comment = newMissingComment;

            this.initialValues.motif_absence_evaluation = newMissingReason;
            this.initialValues.notation_missing_comment = newMissingComment;
            this.initialValues.etat_technique = newTechnicalState;

            if (this.currentInitialValues) {
              this.currentInitialValues.motif_absence_evaluation = newMissingReason;
              this.currentInitialValues.notation_missing_comment = newMissingComment;
              this.currentInitialValues.etat_technique = newTechnicalState;
            }

            if (this.summaryTabComponent) {
              this.summaryTabComponent.loadTechnicalState();
            }

            if (this.asset?.id) {
              this.refreshAssetDataInternal(true).subscribe();
            }

            this.assetsChanges.pendingChangesNumber$.next(0);
          }

          if (hasMaintenanceEvaluationChange) {
            const newMaintenanceEvaluation = this.currentForm.get('maintenance_evaluation')?.value || '';

            this.initialValues.maintenance_evaluation = newMaintenanceEvaluation;
            if (this.currentInitialValues) {
              this.currentInitialValues.maintenance_evaluation = newMaintenanceEvaluation;
            }

            if (this.summaryTabComponent && this.summaryTabComponent.initialValues) {
              this.summaryTabComponent.initialValues.maintenance_evaluation = newMaintenanceEvaluation;
            }

            if (this.summaryTabComponent) {
              this.summaryTabComponent.loadMaintenanceEvaluation();
            }

            if (this.asset?.id) {
              this.refreshAssetDataInternal(true).subscribe(() => {
                if (this.summaryTabComponent) {
                  this.summaryTabComponent.loadMaintenanceEvaluation();
                  this.cdr.detectChanges();
                }
              });
            }

            if (regularChanges.length === 0 && !hasTechnicalStateOrReasonsChange && !hasMissingNotationChange) {
              this.assetsChanges.pendingChangesNumber$.next(0);
            }
          }

          this.loading = false;
          this.isButtonLoading = false;
          this.isConfirmationModalOpen = false;
          this.setSaved();

          this.cdr.detectChanges();

          this.updateSummaryAlerts();

        },
        error: (error) => {
          console.error('[PROCEED WITH SUBMIT] Error during save operation:', error);
          this.loading = false;
          this.isButtonLoading = false;
          this.isConfirmationModalOpen = false;
          this.saveToast.close();

          this.cdr.detectChanges();

          const errorMessage = error?.error?.message || this.translate.instant('An error occurred while saving');
          const iconConfig: NbIconConfig = { icon: "alert-circle-outline", pack: "eva" };
          this.toastrService.show(null, errorMessage, {
            icon: iconConfig,
            status: "danger",
            duration: 5000
          });
        },
        complete: () => {}
      });
    } else {
      this.loading = false;
      this.isConfirmationModalOpen = false;
      this.saveToast.close();
    }
  }

  private handleDescriptifSubmission(): void {
    const changes: { property: string; value: any }[] = Object.keys(this.currentInitialValues)
      .filter(key => this.fieldValueChanged(key))
      .map((key: string) => ({
        property: key,
        value: this.currentForm.get(key)?.value
      }));
    const quantityChanged = changes.some(change => change.property === "quantity");

    if (changes.length > 0) {
      if (quantityChanged) {
        this.subscriptions.push(
          this.showQuantityReplacementInvestmentDialogIfNeeded().subscribe()
        );
      }

      changes.forEach(change => {
        this.initialValues[change.property] = change.value;
        if (this.currentInitialValues) {
          this.currentInitialValues[change.property] = change.value;
        }
      });

      this.assetsChanges.addChanges(this.asset.id, changes);

      this.assetsChanges.pendingChangesNumber$.pipe(
        filter(nb => !nb),
        first()
      ).subscribe(() => {
        changes.forEach(change => {
          this.asset[change.property] = change.value;
        });

        const finishSave = () => {
          this.loading = false;
          this.isButtonLoading = false;
          this.setSaved();
          this.updateDescriptifAlerts();
        };

        if (quantityChanged) {
          this.subscriptions.push(
            this.refreshAssetInvestments().pipe(tap(() => finishSave())).subscribe()
          );
        } else {
          finishSave();
        }
      });
    } else {
      this.loading = false;
      this.isButtonLoading = false;
      this.saveToast.close();
    }
  }

  private handleMaintenanceSubmission(): void {
    if (!this.maintenanceTabComponent) {
      this.loading = false;
      this.isButtonLoading = false;
      this.saveToast.close();
      return;
    }

    this.maintenanceTabComponent.saveMaintenanceRecord().subscribe({
      next: maintenanceRecord => {
        (this.asset as any).maintenance = maintenanceRecord;

        this.currentInitialValues = { ...this.maintenanceTabComponent.initialValues };
        this.maintenanceTabComponent.initialValues = { ...this.maintenanceTabComponent.initialValues };
        this.refreshAssetDataInternal(true).subscribe((updatedAsset: any) => {
          if (updatedAsset) {
            this.maintenanceTabComponent.ngOnChanges({
              asset: { currentValue: this.asset, previousValue: null, firstChange: false, isFirstChange: () => false }
            });
            this.cdr.detectChanges();
          }
        });

        this.assetsChanges.pendingChangesNumber$.next(1);
        setTimeout(() => {
          this.assetsChanges.pendingChangesNumber$.next(0);
        }, 100);

        this.loading = false;
        this.isButtonLoading = false;
        this.setSaved();
        this.cdr.detectChanges();

      },
      error: error => {
        this.loading = false;
        this.isButtonLoading = false;
        this.saveToast.close();
        console.error("Error saving maintenance record:", error);

        this.toastrService.show(null, this.translate.instant("Error saving maintenance record"), {
          status: "danger",
          duration: 5000
        });
      }
    });
  }

  private handleMergedTabSubmission(): void {
    // Determine which form(s) to save based on currentForm
    // The currentForm points to whichever tab component last emitted formChange

    // Check if it's a maintenance form (has maintenance-specific fields)
    const isMaintenance = this.currentForm?.get('maintenance_contract') !== null;

    // Check if it's a compliance form (has compliance-specific fields)
    const isCompliance = this.currentForm?.get('control_frequency') !== null;

    if (isMaintenance) {
      this.handleMaintenanceSubmission();
    } else if (isCompliance) {
      this.handleComplianceSubmission();
    } else {
      // Fallback: no recognized form
      this.loading = false;
      this.isButtonLoading = false;
      this.saveToast.close();
    }
  }

  private handleComplianceSubmission(): void {
    if (!this.complianceTabComponent) {
      this.loading = false;
      this.isButtonLoading = false;
      this.saveToast.close();
      return;
    }

    this.complianceTabComponent
      .saveComplianceRecord()
      .pipe(
        switchMap(complianceRecord => {
          (this.asset as any).compliance = complianceRecord;

          return this.complianceTabComponent.saveReservations().pipe(
            switchMap(_reservationsResult => {
              return new Promise(resolve => resolve(complianceRecord));
            })
          );
        })
      )
      .subscribe({
        next: _complianceRecord => {
          this.currentInitialValues = { ...this.complianceTabComponent.initialValues };


          this.refreshAssetDataInternal(true).subscribe((updatedAsset: any) => {
            if (updatedAsset) {
              this.complianceTabComponent.ngOnChanges({
                asset: { currentValue: this.asset, previousValue: null, firstChange: false, isFirstChange: () => false }
              });
              this.cdr.detectChanges();
            }
          });

          this.assetsChanges.pendingChangesNumber$.next(1);
          setTimeout(() => {
            this.assetsChanges.pendingChangesNumber$.next(0);
          }, 100);

          this.loading = false;
          this.isButtonLoading = false;
          this.setSaved();
          this.cdr.detectChanges();

        },
        error: error => {
          this.loading = false;
          this.isButtonLoading = false;
          this.saveToast.close();
          console.error("Error saving compliance record or reservations:", error);

          this.toastrService.show(null, this.translate.instant("Error saving compliance record"), {
            status: "danger",
            duration: 5000
          });
        }
      });
  }

  public anyFieldValueChanged(): boolean {
    if (!this.currentForm || !this.currentInitialValues) {
      return false;
    }

    try {
      const anyFieldChanged = Object.keys(this.currentInitialValues).some((key: string) => this.fieldValueChanged(key));


      const result = anyFieldChanged;
      return result;
    } catch (error) {
      console.error("Error in anyFieldValueChanged:", error);
      return false;
    }
  }

  public goToAudit(): void {
    const params = {
      asset: this.asset,
      siteId: this.asset.perimeters[0].id,
      dialogCtrl: this.dialogService
    };
    this.assetsService.openAuditAssetEditor(params);
  }

  public openAssetSheet(): void {
    if (this.isSheetDialogOpen) {
      return;
    }

    this.isSheetDialogOpen = true;

    import("../sheet-dialog/sheet-dialog.component").then(module => {
      const dialogRef = this.dialogService.open(module.SheetDialogComponent, {
        autoFocus: false,
        context: {
          assetId: this.asset.id,
          title: this.translate.instant("Asset details")
        }
      });

      dialogRef.onClose.subscribe(() => {
        this.isSheetDialogOpen = false;
      });
    });
  }

  public isFormValid(): boolean {
    if (!this.currentForm) return false;

    try {
      const formValid = this.currentForm.valid || false;
      const technicalStateValid = this.summaryTabComponent?.isTechnicalStateValid !== false;
      return formValid && technicalStateValid;
    } catch (error) {
      console.error("Error in isFormValid:", error);
      return false;
    }
  }

  public onClose(): void {
    // Skip confirmation dialog for auto-save tabs (changes are already saved)
    if (this.anyFieldValueChanged() && !this.shouldAutoSave()) {
      this.dialogService.open(
        SideFormDialogComponent,
        {
          autoFocus: false,
          closeOnBackdropClick: false,
          context: {
            title: this.translate.instant('Unregistered changes'),
            content: this.translate.instant('Would you like to exit ?'),
          },
        }
      ).onClose.subscribe((confirmation: boolean) => {
        if (confirmation) {
          this.ref.close();
        this.drawerClosed.emit();
      }
      });
    } else {
      this.ref.close();
      this.drawerClosed.emit();
    }
  }

  public toggleFullScreen(fullScreen: boolean): void {
    this.isFullScreen = fullScreen;
  }

  protected fieldValueChanged(key: string): boolean {
    if (!this.currentForm || !this.currentInitialValues) return false;

    try {
      const formControl = this.currentForm.get(key);
      if (!formControl) return false;
      const currentValue = formControl.value;
      const initialValue = this.currentInitialValues[key];

      const normalizeValue = (val: any): any => {
        if (val === null || val === undefined || val === '') return null;

        if (val && typeof val === 'object' && (val as any)._isAMomentObject) {
          return (val as any).format('YYYY-MM-DD');
        }

        if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}/)) {
          return val;
        }

        return val;
      };

      const normalizedCurrent = normalizeValue(currentValue);
      const normalizedInitial = normalizeValue(initialValue);

      if (normalizedCurrent === null && normalizedInitial === null) {
        return false;
      }

      if (Array.isArray(normalizedCurrent) && Array.isArray(normalizedInitial)) {
        if (normalizedCurrent.length !== normalizedInitial.length) return true;
        const sortedCurrent = [...normalizedCurrent].sort((a, b) => a - b);
        const sortedInitial = [...normalizedInitial].sort((a, b) => a - b);
        return sortedCurrent.some((val, idx) => val !== sortedInitial[idx]);
      }

      return normalizedCurrent !== normalizedInitial;
    } catch (error) {
      console.error("Error in fieldValueChanged for key:", key, error);
      return false;
    }
  }

  public setActiveTab(tab: string): void {
    if (tab === "maintenance" && !this.maintenanceManagementEnabled) {
      return;
    }
    if (tab === "conformite" && !this.complianceManagementEnabled) {
      return;
    }

    if (this.activeTab !== tab) {
      this.currentForm = null;
      this.currentInitialValues = null;
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer);
        this.autoSaveTimer = null;
      }
    }

    this.activeTab = tab;
  }

  public isActiveTab(tab: string): boolean {
    return this.activeTab === tab;
  }

  public shouldAutoSave(): boolean {
    if (this.activeTab === "descriptif") {
      return true;
    }
    if (this.activeTab === "conformite-maintenance") {
      return true;
    }
    return false;
  }

  private triggerAutoSave(): void {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }

    this.autoSaveTimer = setTimeout(() => {
      const shouldAutoSave = this.shouldAutoSave();
      const hasChanges = this.anyFieldValueChanged();
      const isValid = this.isFormValid();

      // Avoid auto-saving lifecycle/technical-state-only changes
      // (the user typically wants to save those manually).
      const lifecycleFields = new Set([
        'motif_evaluation',
        'etat_technique',
        'motif_absence_evaluation',
        'nb_annees_restantes',
        'notation_missing_comment'
      ]);

      let onlyLifecycleChanges = false;
      try {
        if (hasChanges && this.currentInitialValues) {
          const changedKeys = Object.keys(this.currentInitialValues).filter(k => this.fieldValueChanged(k));
          if (changedKeys.length > 0 && changedKeys.every(k => lifecycleFields.has(k))) {
            onlyLifecycleChanges = true;
          }
        }
      } catch (e) {
        onlyLifecycleChanges = false;
      }

      if (shouldAutoSave && hasChanges && isValid && !this.isConfirmationModalOpen && !onlyLifecycleChanges) {
        this.onSubmit(true);
      }
    }, 1000); // 1 second debounce
  }

  private setSaving(): void {
    this.saveToast.showSaving();
  }

  private setSaved(): void {
    this.saveToast.showSaved();
  }

  public openInvestmentForm(): void {
    this.investmentIds = this.extractInvestmentIds(this.asset?.investments);
    this.investmentData = this.asset?.investments || [];
    this.investmentDrawerVisible = true;
  }

  public openCostDialog(): void {
    this.openInvestmentForm();
  }

  public onInvestmentDrawerClosed(): void {
    this.investmentDrawerVisible = false;
    this.investmentIds = [];
    this.investmentData = [];
  }

  public onInvestmentSelected(investmentId: number): void {
    this.openInvestmentDetail(investmentId);
  }

  public onInvestmentsAttached(investmentIds: number[]): void {
    if (!this.asset?.id) {
      return;
    }

    if (investmentIds === null) {
      this.refreshAssetInvestments().subscribe();
      return;
    }

    const currentInvestmentIds = this.extractInvestmentIds(this.asset.investments);
    const newIds = investmentIds.filter(id => !currentInvestmentIds.includes(id));

    if (newIds.length === 0) {
      return;
    }

    this.setSaving();
    const operations: Observable<any>[] = newIds.map(id => this.createOperation(id, false));

    combineLatest(operations).pipe(
      first(),
    ).subscribe(results => {
      // Load the investment only after attachment succeeds, so its asset data is current.
      const attachedIds = newIds.filter((_id, index) => results[index]?.success);
      this.updateInvestmentDataOptimistically([...currentInvestmentIds, ...attachedIds]);

      const failed = results.find(result => result?.error === 'failed');
      if (failed) {
        this.saveToast.showError(failed.message);
      } else {
        this.setSaved();
      }
    });
  }

  private updateInvestmentDataOptimistically(targetIds: number[]): void {
    this.investmentIds = targetIds;
    this.assetInvestmentsTotal = targetIds.length;

    if (targetIds.length === 0) {
      this.investmentData = [];
      if (this.asset) {
        this.asset.investments = [];
      }
      this.cdr.detectChanges();
      return;
    }

    const fetchObservable = targetIds.length === 1
      ? this.investmentsService.getInvestment(targetIds[0]).pipe(
          map((investment: any) => investment ? [investment] : []),
          catchError(() => of([] as any[]))
        )
      : this.investmentsService.getInvestmentsByIds(targetIds).pipe(
          catchError(() => of([] as any[]))
        );

    fetchObservable.pipe(first()).subscribe(investments => {
      this.investmentData = investments || [];
      if (this.asset) {
        this.asset.investments = this.investmentData;
      }
      this.cdr.detectChanges();
    });
  }

  private createOperation(investmentId: number, detach: boolean): Observable<any> {
    const payload = this.createPayload(investmentId, detach);
    return this.api.investments_attach_investment(payload).pipe(
      first(),
      switchMap((result: any) => {
        if (result && result.ok === false) {
          const errorMsg = result.errors || this.translate.instant(
            detach ? 'Failed to detach investment' : 'Failed to attach investment'
          );
          return of({ error: 'failed', message: errorMsg });
        }
        return of({ success: true });
      }),
      catchError(error => {
        const errorMessage = error?.error?.errors || error?.error?.message ||
          this.translate.instant(detach ? 'Failed to detach investment' : 'Failed to attach investment');
        return of({ error: 'failed', message: errorMessage });
      })
    );
  }

  private createPayload(investmentId: number, detach: boolean): {
    asset_id: number;
    investment_id?: number;
    investment_local_id?: string;
    asset_local_id?: string;
    detach?: boolean;
  } {
    const payload: any = {
      asset_id: this.asset.id,
      investment_id: investmentId
    };

    if (detach) {
      payload.detach = true;
    }

    if ((this.asset as any).local_id) {
      payload.asset_local_id = (this.asset as any).local_id;
    }

    return payload;
  }

  private refreshAssetInvestments(): Observable<any> {
    if (!this.asset?.id) {
      return of(null);
    }

    return this.assetsService.getAsset(this.asset.id).pipe(
      first(),
      switchMap(updatedAsset => {
        if (updatedAsset) {
          this.asset = updatedAsset;
          const updatedIds = this.extractInvestmentIds(updatedAsset.investments);
          this.updateInvestmentDataOptimistically(updatedIds);
        }
        return of(updatedAsset);
      }),
      catchError(() => of(null))
    );
  }

  private getAssetInvestments(): Observable<any[]> {
    const investmentIds = Array.from(new Set(this.extractInvestmentIds(this.asset?.investments || [])));
    if (!investmentIds.length) {
      return of([]);
    }

    const loadedInvestments = (this.asset?.investments || []).filter(
      (investment: any) => investment && typeof investment === "object" && this.hasReplacementDialogData(investment)
    );
    if (loadedInvestments.length === investmentIds.length) {
      return of(loadedInvestments);
    }

    return this.investmentsService.getInvestmentsByIds(investmentIds).pipe(
      first(),
      map(investments => (investments || []).filter(investment => !!investment)),
      catchError(() => of([]))
    );
  }

  private hasReplacementDialogData(investment: any): boolean {
    return !!(
      this.getInvestmentType(investment) &&
      this.getInvestmentStatus(investment) &&
      investment.budget_origin &&
      Array.isArray(investment.slices)
    );
  }

  private getInvestmentType(investment: any): KPI.InvestmentType | null {
    return investment?.investment_type_object ||
      (typeof investment?.investment_type === "object" ? investment.investment_type : null);
  }

  private getInvestmentStatus(investment: any): KPI.InvestmentStatus | null {
    return investment?.status_object ||
      (typeof investment?.status === "object" ? investment.status : null);
  }

  private getInvestmentStatusId(investment: any): number {
    const status = investment?.status;
    return Number(typeof status === "object" ? status.id : status);
  }

  private isReplacementInvestment(investment: any): boolean {
    const investmentAssetId = Number(investment?.asset_object?.id ?? investment?.asset?.id ?? investment?.asset);
    const investmentType = this.getInvestmentType(investment);
    return !!(
      investment &&
      investmentAssetId === Number(this.asset?.id) &&
      investmentType?.replacement
    );
  }

  private isPriceSheetReplacementInvestment(investment: any): boolean {
    return !!(
      this.isReplacementInvestment(investment) &&
      investment.budget_origin?.price_list
    );
  }

  private isHypothesisReplacementInvestment(investment: any): boolean {
    const status = this.getInvestmentStatus(investment);
    return !!(
      this.isReplacementInvestment(investment) &&
      status?.hypothesis &&
      !status?.is_locked
    );
  }

  private hasReplacementDateGap(investment: any): boolean {
    const assetEndOfLifeYear = this.getAssetEndOfLifeYear();
    if (!assetEndOfLifeYear) {
      return false;
    }

    const statusId = this.getInvestmentStatusId(investment);
    const currentStatusSlices = (investment.slices || []).filter(slice =>
      Number((slice.status as any)?.id ?? slice.status) === statusId
    );
    const sliceYears = (currentStatusSlices.length ? currentStatusSlices : investment.slices || [])
      .map(slice => Number(slice.year))
      .filter(year => Number.isFinite(year));

    return sliceYears.some(year => year !== assetEndOfLifeYear);
  }

  private getAssetEndOfLifeYear(): number {
    const remainingYears = Number(this.currentForm?.get('nb_annees_restantes')?.value);
    if (Number.isFinite(remainingYears)) {
      return new Date().getFullYear() + remainingYears;
    }

    return Number(this.asset?.end_of_life || 0);
  }

  private isAutoUpdatablePriceSheetReplacementInvestment(investment: any): boolean {
    const status = this.getInvestmentStatus(investment);
    const statusId = this.getInvestmentStatusId(investment);
    return !!(
      this.isPriceSheetReplacementInvestment(investment) &&
      status?.hypothesis &&
      !status?.is_locked &&
      (investment.slices || []).filter(slice =>
        Number((slice.status as any)?.id ?? slice.status) === statusId
      ).length === 1
    );
  }

  private showReplacementInvestmentDialog(
    message: string,
    title: string = "Replacement investment"
  ): void {
    this.dialogService.open(BasicDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      context: {
        canCancel: false,
        title: this.translate.instant(title),
        content: this.translate.instant(message)
      }
    });
  }

  private showQuantityReplacementInvestmentDialogIfNeeded(): Observable<void> {
    return this.getAssetInvestments().pipe(
      tap(investments => {
        const replacementWithPriceList = investments.filter(investment =>
          this.isPriceSheetReplacementInvestment(investment)
        );
        if (!replacementWithPriceList.length) {
          return;
        }

        const hasAutoUpdatableInvestment = replacementWithPriceList.some(investment =>
          this.isAutoUpdatablePriceSheetReplacementInvestment(investment)
        );

        const message = hasAutoUpdatableInvestment
          ? "You changed the equipment quantity, so the budget of the replacement investment will be automatically updated"
          : "You changed the equipment quantity, remember to update budget of the replacement investment";
        this.showReplacementInvestmentDialog(message);
      }),
      map(() => undefined)
    );
  }

  private showLifecycleReplacementDialogForInvestments(investments: any[]): void {
    const hasHypothesisReplacementInvestment = investments.some(investment =>
      this.isHypothesisReplacementInvestment(investment) &&
      this.hasReplacementDateGap(investment)
    );
    if (hasHypothesisReplacementInvestment) {
      this.showReplacementInvestmentDialog(
        "The investment replacement date will be updated automatically.",
        "Modified asset end of life year"
      );
    }
  }

  private extractInvestmentIds(investments: any[]): number[] {
    return (investments || []).map((inv: any) =>
      typeof inv === 'object' && inv !== null ? inv.id : inv
    ).filter((id: any) => id !== null && id !== undefined);
  }

  private openInvestmentDetail(investmentId: number): void {
    const subscription = this.investmentsService.getInvestment(investmentId).subscribe({
      next: investment => {
        if (investment) {
          const dialogRef = this.rightPanelDialogService.open(InvestmentFormComponent, {
            closeOnBackdropClick: false,
            hasScroll: true,
            context: {
              investment: investment as KPI.MappedInvestment,
            }
          });

          dialogRef.onClose.subscribe((result) => {
            if (result && result !== null && !result.deleted) {
              this.refreshInvestmentList();
            }
          });
        }
      },
    });

    this.subscriptions.push(subscription);
  }

  private refreshInvestmentList(): void {
    if (!this.asset?.id) {
      return;
    }
    // Use investmentIds path to trigger full investment fetch (loadInvestments)
    // instead of investmentData which uses simplified asset.investments objects
    const ids = this.extractInvestmentIds(this.asset?.investments);
    this.investmentData = [];
    this.investmentIds = [...ids];
    this.cdr.detectChanges();
  }

  public openImagePreview(picture: KPI.AssetPicture): void {
    const fullSizeImage = picture.picture?.trim() || null;
    const imageUrl = fullSizeImage || picture.thumbnail?.trim() || null;

    this.dialogService.open(ImagePreviewDialogComponent, {
      autoFocus: false,
      hasBackdrop: true,
      closeOnBackdropClick: true,
      hasScroll: false,
      backdropClass: 'image-preview-backdrop',
      dialogClass: 'image-preview-dialog-container',
      context: {
        imageUrl: imageUrl,
        imageAlt: this.asset.label,
        imageTitle: this.asset.label
      }
    });
  }

  public openIniesModal(): void {
    if (this.iniesDataSheets.length > 0) {
      this.showIniesModal = true;
    }
  }

  public closeIniesModal(): void {
    this.showIniesModal = false;
  }

  public copyToClipboard(url: string): void {
    navigator.clipboard
      .writeText(url)
      .catch(err => {
        console.error("Failed to copy URL:", err);
      });
  }

  public isAssetValidated(): boolean {
    return (this.asset as any)?.equipment_validated === true;
  }

  public shouldShowCloudUploadIcon(): boolean {
    return this.assetEquipmentAndReserveXlsExport === true
      && this.assetEquipmentValidation === true
      && this.isAssetValidated();
  }

  public shouldShowCloudOffIcon(): boolean {
    return this.assetEquipmentAndReserveXlsExport === true
      && this.assetEquipmentValidation === true
      && !this.isAssetValidated();
  }

  public shouldShowAlertCounts(): boolean {
    return this.settingsService.settingMustBeTrue('ASSET__EQUIPMENT_VALIDATION');
  }

  private saveMaintenanceEvaluation(): Observable<any> {
    const newValue = this.currentForm.get('maintenance_evaluation')?.value || '';
    const maintenance = (this.asset as any).maintenance;

    if (!maintenance || !maintenance.id) {
      const createPayload: any = {
        asset: this.asset.id,
        equipment_evaluation: newValue,
        last_evaluation: newValue,
        evaluation_date: new Date().toISOString().split("T")[0],
        attached_to_equipment: true,
        has_contract: null,
        company: "",
        contract_deadline: null,
        level: null,
        responsibility: "",
        manufacturer_control_frequency: "",
        control_frequency: "",
        last_control_date: null,
        evaluated_by: "",
        comment: ""
      };

      return this.maintenanceService.createAssetMaintenance(createPayload).pipe(
        timeout(10000),
        tap((createdMaintenance) => {
          (this.asset as any).maintenance = createdMaintenance;
          if (this.currentInitialValues) {
            this.currentInitialValues.maintenance_evaluation = newValue;
          }
        }),
        catchError((error) => {
          console.error('Error creating maintenance record:', error);
          return of(null);
        })
      );
    }

    const maintenanceRecordId = maintenance.id;
    const payload = { equipment_evaluation: newValue };

    return this.maintenanceService.patchAssetMaintenance(maintenanceRecordId, payload).pipe(
      timeout(10000),
      tap(() => {
        (this.asset as any).maintenance.equipment_evaluation = newValue;
        if (this.currentInitialValues) {
          this.currentInitialValues.maintenance_evaluation = newValue;
        }
      }),
      catchError((error) => {
        console.error('Error saving maintenance evaluation:', error);
        return of(null);
      })
    );
  }
}
